import { Galaxy, StarEffect, StarFilterInput, StarTier } from '@apeiron/star'
import { Box, styled } from '@mui/material'
import {
  ChipIcon,
  ChipLabel,
  ChipToggleButton,
  ChipToggleButtonGroup
} from '@src/components/share/filter/ChipToggle'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle,
  FilterTitle
} from '@src/components/share/filter/FilterSection'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import useFetchStarFilterJson from '@src/hooks/json/useFetchStarFilterJson'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding: 10px 10px 10px 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
`

const Section = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Title = FilterTitle

const StarTypeAndEffectFilter: FC<Props> = (props: Props) => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const [tiersData, setTiersData] = useState<StarTier[]>([])

  const [effectData, setEffectData] = useState<StarEffect[]>([])

  const [galaxiesData, setGalaxiesData] = useState<Galaxy[]>([])

  const { data } = useFetchStarFilterJson()

  const tiers = useMemo(() => filterInput.tier ?? [], [filterInput.tier])

  const effect = useMemo(
    () => filterInput.coreEffect ?? [],
    [filterInput.coreEffect]
  )

  const galaxies = useMemo(() => filterInput.galaxy ?? [], [filterInput.galaxy])

  const numberOfFilters = useMemo(() => {
    const countFilters = (key: string, filter: StarFilterInput): number => {
      const doCountFilters = R.pipe<any, any[], number>(
        R.propOr([], key),
        R.length
      )

      return doCountFilters(filter)
    }

    const numberOfEffectFilters = countFilters('coreEffect', filterInput)

    const numberOfTierFilters = countFilters('tier', filterInput)

    const numberOfGalaxyFilters = countFilters('galaxy', filterInput)

    return R.sum([
      numberOfEffectFilters,
      numberOfTierFilters,
      numberOfGalaxyFilters
    ])
  }, [filterInput])

  const handleOnReset = useCallback(() => {
    onFilterChange(R.omit(['coreEffect', 'galaxy', 'tier'], filterInput))
  }, [filterInput, onFilterChange])

  const handleOnTiersChange = useCallback(
    (_: any, input: number[]) => {
      onFilterChange(R.assoc('tier', input, filterInput))
    },
    [filterInput, onFilterChange]
  )

  const handleOnEffectChange = useCallback(
    (_: any, input: number[]) => {
      onFilterChange(R.assoc('coreEffect', input, filterInput))
    },
    [filterInput, onFilterChange]
  )

  const handleOnGalaxyChange = useCallback(
    (_: any, input: number[]) => {
      onFilterChange(R.assoc('galaxy', input, filterInput))
    },
    [filterInput, onFilterChange]
  )

  useEffect(() => {
    setTiersData(R.propOr([], 'starFilterTiers', data))
    setEffectData(R.propOr([], 'starFilterEffect', data))
    setGalaxiesData(R.propOr([], 'starFilterGalaxies', data))
  }, [data])

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>
          {t('star.list.filter.type_and_effect.title')}
        </FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        {!R.isEmpty(tiersData) && (
          <Section>
            <Title>{t('star.list.filter.type_and_effect.star_tier')}</Title>
            <ChipToggleButtonGroup value={tiers} onChange={handleOnTiersChange}>
              {tiersData.map((tier: StarTier) => (
                <ChipToggleButton key={tier.level} value={tier.level}>
                  <ChipIcon src={tier.image} />
                  <ChipLabel>{tier.type}</ChipLabel>
                </ChipToggleButton>
              ))}
            </ChipToggleButtonGroup>
          </Section>
        )}
        {!R.isEmpty(effectData) && (
          <Section>
            <Title>{t('star.list.filter.type_and_effect.star_effect')}</Title>
            <ChipToggleButtonGroup
              value={effect}
              onChange={handleOnEffectChange}
            >
              {effectData.map((data: StarEffect) => (
                <ChipToggleButton key={data.name} value={data.id}>
                  <ChipIcon src={data.image} />
                  <ChipLabel>{data.name}</ChipLabel>
                </ChipToggleButton>
              ))}
            </ChipToggleButtonGroup>
          </Section>
        )}
        {!R.isEmpty(galaxiesData) && (
          <Section>
            <Title>{t('star.list.filter.type_and_effect.galaxy')}</Title>
            <ChipToggleButtonGroup
              value={galaxies}
              onChange={handleOnGalaxyChange}
            >
              {galaxiesData.map((galaxyData: Galaxy) => (
                <ChipToggleButton key={galaxyData.name} value={galaxyData.id}>
                  <ChipIcon src={galaxyData.image} />
                  <ChipLabel>{galaxyData.name}</ChipLabel>
                </ChipToggleButton>
              ))}
            </ChipToggleButtonGroup>
          </Section>
        )}
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: StarFilterInput
  onFilterChange: (input: StarFilterInput) => void
}

export default StarTypeAndEffectFilter
